import { Link } from 'react-router-dom';
import { usePublicConfig } from '../PublicShell';
import { LegalLayout, Section, List, Table } from './LegalLayout';

/**
 * The Article 28 processor terms.
 *
 * Note the sub-processor table is named in full. Product screens and marketing
 * pages deliberately do not name suppliers — customers have no decision to make
 * there and it only invites noise. This document is the exception, and it is not
 * a stylistic one: UK GDPR Article 28(2) requires a processor to inform the
 * controller of sub-processors and give them a chance to object. A DPA that
 * hides them is not a compliant DPA.
 */
export default function DataProcessing() {
  const c = usePublicConfig();
  const company = c.company.name;
  const product = c.company.product;

  return (
    <LegalLayout
      title="Data processing agreement"
      updated="11 September 2026"
      intro={
        <>
          These are the Article 28 terms on which {company} processes personal data on behalf
          of {product} customers. They form part of the{' '}
          <Link to="/terms">terms of service</Link> and apply automatically to every account —
          there is nothing separate to sign.
        </>
      }
    >
      <Section title="1. Roles">
        <p>
          You are the <strong className="text-ink">controller</strong>. You decide what tenant
          data to hold, why, and for how long. {company} is the{' '}
          <strong className="text-ink">processor</strong>, acting only on your instructions.
        </p>
        <p>
          &ldquo;UK GDPR&rdquo; means the retained EU General Data Protection Regulation as it
          forms part of UK law, read with the Data Protection Act 2018.
        </p>
      </Section>

      <Section title="2. Scope of processing">
        <Table
          head={['', 'Detail']}
          rows={[
            ['Subject matter', <>Providing {product} to you</>],
            ['Duration', 'For as long as your account is open, plus the retention periods below'],
            ['Nature and purpose', 'Storing, organising, retrieving and transmitting data so you can manage your properties and tenancies'],
            ['Types of data', 'Names, contact details, dates of birth, identification details and documents, employment details, emergency contacts, tenancy and financial records, uploaded documents, message logs'],
            ['Categories of data subject', 'Your tenants and applicants, your staff, and your contractors'],
            ['Special category data', 'Not required by the service. May be present incidentally in identification documents you choose to upload'],
          ]}
        />
      </Section>

      <Section title="3. Our obligations">
        <List
          items={[
            'We process personal data only on your documented instructions, which include your use of the service and any configuration you set. Using the product is an instruction.',
            'If we believe an instruction breaches data protection law, we will tell you and may decline to act on it.',
            'Everyone with access is bound by confidentiality obligations that survive the end of their engagement.',
            'We keep appropriate technical and organisational security measures — see section 6.',
            'We assist you, so far as is reasonable, with data subject requests, impact assessments and consultations with the ICO.',
            'We notify you without undue delay, and in any event within 48 hours, of becoming aware of a personal data breach affecting your data.',
            'We make available the information you need to demonstrate compliance, and allow audits as set out in section 8.',
          ]}
        />
      </Section>

      <Section title="4. Sub-processors">
        <p>
          You give general authorisation for us to use the sub-processors listed below. Each is
          bound by written terms no less protective than these.
        </p>
        <Table
          head={['Supplier', 'What they do', 'Where']}
          rows={[
            ['Google Ireland Ltd / Google LLC', 'Application hosting, database and document storage', 'EU and US'],
            ['Netlify Inc.', 'Serving the website', 'US, with a global content network'],
            ['Twilio Ireland Ltd / Twilio Inc.', 'Sending text messages and, where configured, email', 'EU and US'],
            ['tawk.to Inc.', 'Live chat on our own help page, where it is switched on. It sees what the person types to us, not the records on the page.', 'US'],
          ]}
        />
        <p>
          We will give you at least 30 days&rsquo; notice by email before adding or replacing a
          sub-processor. If you reasonably object on data protection grounds, tell us within
          that period and we will try to find an alternative. If we cannot, you may terminate
          the affected part of the service and receive a pro-rata refund.
        </p>
      </Section>

      <Section title="5. International transfers">
        <p>
          Where a sub-processor processes data outside the UK, the transfer is covered by the
          UK International Data Transfer Addendum to the EU Standard Contractual Clauses, or by
          the supplier&rsquo;s certification under the UK extension to the EU&ndash;US Data
          Privacy Framework. We do not transfer personal data to a country without such
          safeguards.
        </p>
      </Section>

      <Section title="6. Security measures">
        <List
          items={[
            'Encryption of data in transit using TLS.',
            'Encryption of data at rest by the underlying storage platform.',
            'Passwords stored only as salted, iterated hashes — never recoverable.',
            'Signed session tokens with a fixed expiry.',
            'Role-based access control within each account, with four permission levels.',
            'Documents stored privately and served only through the application’s permission checks; never as a public link.',
            'An append-only audit log of actions affecting money.',
            'Access to production systems limited to personnel who need it.',
          ]}
        />
        <p>
          We may update these measures, but not in a way that materially reduces the level of
          protection.
        </p>
      </Section>

      <Section title="7. Return and deletion">
        <p>
          You can export your data at any time from the application, and delete your entire
          organisation from Settings. On deletion, records are removed immediately; stored
          documents are moved to a recovery area and destroyed within 30 days.
        </p>
        <p>
          On termination we delete your data within 30 days unless you ask us in writing to
          return it first. Backups are overwritten on a rolling 90-day cycle. We may keep data
          where UK law requires it, and it stays protected by these terms while we do.
        </p>
      </Section>

      <Section title="8. Audit">
        <p>
          On reasonable written notice, and no more than once in any twelve months unless a
          breach or a regulator requires otherwise, we will provide the information reasonably
          needed to demonstrate compliance with this agreement. Where a documented review is
          not sufficient, we will cooperate with an audit at your cost, conducted so as not to
          disrupt the service or compromise other customers&rsquo; confidentiality.
        </p>
      </Section>

      <Section title="9. Data subject requests">
        <p>
          If a tenant contacts us directly, we will not respond substantively. We will tell them
          to contact you and let you know it happened, unless the law prevents us. You are
          responsible for answering — the application gives you access to everything held about
          them, which is what you need to do so.
        </p>
      </Section>

      <Section title="10. Liability and precedence">
        <p>
          Liability under this agreement is subject to the limits in the{' '}
          <Link to="/terms">terms of service</Link>. Where this agreement conflicts with those
          terms on the processing of personal data, this agreement prevails.
        </p>
      </Section>

      <Section title="11. Contact">
        <p>
          Data protection enquiries and breach notifications:{' '}
          <a href={`mailto:${c.contact.privacy}`}>{c.contact.privacy}</a>
        </p>
        {c.company.address && <p>{c.company.address}</p>}
      </Section>
    </LegalLayout>
  );
}
